import { useContext, useEffect, useRef } from 'react'
import { AppContext, AppContextType } from '../../AppContext'
import { MediaSources, UserContext, UserContextType } from '../../UserContext'
import { logger } from '../../services/Logger'
import { getAudioAndVideoMediaSources } from '../../services/MediaDevices'
import useUserContextRef from '../../services/useUserContextRef'

const log = logger.createChildLogger({ name: 'MediaSourcesChangeReactionService' })

export default function useMediaSourcesChangedEventHandlerService() {
    const { state: appState } = useContext(AppContext) as AppContextType
    const { dispatch } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    const updatedByEventCtRef = useRef<number>(0)

    /**
     * Listens for new/removed devices and updates `UserContext.state.mediaSources`.
     *
     * This is done by registering a callback to the 'devicechange' event in
     * `navigator.mediaDevices`.
     */
    useEffect(() => {
        async function deviceChangeHandlerDebounced() {
            // `updatedCt == 0` means that no media sources query have been made yet. we do not
            // update the mediaSources here since this deviceChangeHandler should only trigger
            // an update once mediaSources has been populated.
            if (userStateRef.current.mediaSources.updatedCt === 0) {
                return
            }

            const newMediaSources: Partial<MediaSources> = await getAudioAndVideoMediaSources({
                origin: 'deviceChangeHandler',
                forceShowAllAudioDevices: appState.appSettings.forceShowAllAudioDevices,
            })

            // increment 'updatedByEventCt' and 'updatedCt'.
            updatedByEventCtRef.current += 1
            newMediaSources.updatedByEventCt = updatedByEventCtRef.current
            newMediaSources.updatedCt = userStateRef.current.mediaSources.updatedCt + 1

            log.info('registerDeviceChangeHandler', 'deviceChangeHandler triggered.', {
                newMediaSources: stringifyMediaSources(newMediaSources),
                currMediaSources: stringifyMediaSources(userStateRef.current.mediaSources),
                updatedByEventCt: newMediaSources.updatedByEventCt,
            })

            dispatch({
                type: 'update_media_sources',
                value: newMediaSources,
            })
        }

        let t: NodeJS.Timeout | undefined = undefined

        let eventRegisteredTimestamp = new Date().getTime()

        /**
         * If multiple devices are changed simultaneously (for example, when a device with both
         * a speaker and a mic is plugged in), the event may fire once containing all the changes
         * or twice, one-by-one for each device; it is not deterministic.
         */
        const deviceChangeHandler = async (event: Event) => {
            const dtSinceHandlerRegistration = new Date().getTime() - eventRegisteredTimestamp
            if (dtSinceHandlerRegistration < 1000) {
                log.verbose(
                    'registerDeviceChangeHandler',
                    `not handling devicechange event becuase ` +
                        `dtSinceHandlerRegistration=${dtSinceHandlerRegistration} < 1000 -- we ignore ` +
                        `devicechange event which happens right after regsitration.`
                )
                return
            }

            clearTimeout(t)
            t = setTimeout(() => {
                deviceChangeHandlerDebounced()
            }, 1500)
        }

        log.verbose('registerDeviceChangeHandler', `register (navigator.mediaDevices.addEventListener).`)
        navigator.mediaDevices.addEventListener('devicechange', deviceChangeHandler)
        return () => {
            log.verbose('registerDeviceChangeHandler', `deregister (navigator.mediaDevices.removeEventListener).`)
            navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandler)
        }
    }, [appState.appSettings.forceShowAllAudioDevices])

    return {}
}

function stringifyMediaSources(sources: Partial<MediaSources>) {
    const next: any = {}

    if (sources.audioInputs) {
        next.audioInputs = sources.audioInputs.map((a) => a.label)
    }

    if (sources.audioOutputs) {
        next.audioOutputs = sources.audioOutputs.map((a) => a.label)
    }

    return JSON.stringify(next)
}
